import { type ReactNode, useState, useMemo } from 'react';
import { EmptyState } from '@/components/ui/empty-state';
import { useMinLoading } from '@/hooks/use-min-loading';
import type { LeaderboardRow, SortField } from '../types';
import { countActive } from '../utils/leaderboard-active';
import { LeaderboardHeader } from './leaderboard-header';
import { LeaderboardBody } from './leaderboard-body';
import { LeaderboardSkeleton } from './leaderboard-skeleton';

function sortRows(
  rows: LeaderboardRow[],
  field: SortField,
  desc: boolean
): LeaderboardRow[] {
  const key = field === 'quantity' ? 'creatorCount' : field;
  const sorted = [...rows].sort(
    (a, b) => (a[key] as number) - (b[key] as number)
  );
  return desc ? sorted.reverse() : sorted;
}

function useSort(): {
  sf: SortField;
  desc: boolean;
  handleSort: (f: SortField) => void;
} {
  const [sf, setSf] = useState<SortField>('quantity');
  const [desc, setDesc] = useState(true);
  const handleSort = (f: SortField): void => {
    if (f === sf) setDesc((d) => !d);
    else {
      setSf(f);
      setDesc(true);
    }
  };
  return { sf, desc, handleSort };
}

function TableBody({
  showSkeleton,
  sorted,
}: {
  showSkeleton: boolean;
  sorted: LeaderboardRow[];
}): ReactNode {
  if (showSkeleton) return <LeaderboardSkeleton />;
  return <LeaderboardBody rows={sorted} />;
}

function TableShell({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="overflow-hidden rounded-xl bg-white/[0.02]">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-left">{children}</table>
      </div>
    </div>
  );
}

type TableProps = {
  data: LeaderboardRow[] | undefined;
  isLoading: boolean;
  investment: { total: number; goal: number } | undefined;
};

type LoadedProps = {
  data: LeaderboardRow[] | undefined;
  showSkeleton: boolean;
  investment: TableProps['investment'];
};

function LoadedTable({
  data,
  showSkeleton,
  investment,
}: LoadedProps): ReactNode {
  const { sf, desc, handleSort } = useSort();
  const sorted = useMemo(
    () => (data ? sortRows(data, sf, desc) : []),
    [data, sf, desc]
  );
  if (!showSkeleton && !sorted.length) return <EmptyState title="Sin datos" />;
  return (
    <TableShell>
      <LeaderboardHeader
        sortField={sf}
        desc={desc}
        onSort={handleSort}
        total={data?.length}
        active={data ? countActive(data) : undefined}
        investment={investment}
      />
      <TableBody showSkeleton={showSkeleton} sorted={sorted} />
    </TableShell>
  );
}

export function LeaderboardTable(p: TableProps): ReactNode {
  const showSkeleton = useMinLoading(p.isLoading);
  return (
    <LoadedTable
      data={p.data}
      showSkeleton={showSkeleton}
      investment={p.investment}
    />
  );
}
